'use client';

import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  History, Search, ChevronDown, RotateCcw, Eye,
  ChevronLeft, ChevronRight, SortAsc, SortDesc,
} from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';

interface RemediationJob {
  id: string;
  rule: string;
  incidentId: string;
  node: string;
  region: string;
  triggeredBy: string;
  status: 'success' | 'failed' | 'running' | 'queued' | 'rolled-back';
  startedAt: string;
  duration: string;
  rollbackAvailable: boolean;
  output: string;
  executedBy: string;
}

const jobs: RemediationJob[] = [
  { id: 'job-9871', rule: 'BGP-Session-Restart', incidentId: 'INC-4821', node: 'CORE-BOLE-01', region: 'Bole', triggeredBy: 'AI Detection', status: 'running', startedAt: '09:14:45', duration: '9m 40s', rollbackAvailable: true, output: 'BGP soft-reset executed. Awaiting peer re-establishment (8/12 peers up).', executedBy: 'netopsai-engine' },
  { id: 'job-9870', rule: 'Interface-Flap-Dampen', incidentId: 'INC-4820', node: 'EDGE-KIRKOS-04', region: 'Kirkos', triggeredBy: 'Threshold', status: 'running', startedAt: '09:24:01', duration: '0m 54s', rollbackAvailable: true, output: 'Dampening policy applied to GigabitEthernet0/0/1. Monitoring stability.', executedBy: 'netopsai-engine' },
  { id: 'job-9869', rule: 'Traffic-Reroute-ECMP', incidentId: 'INC-4817', node: 'EDGE-LIDETA-03', region: 'Lideta', triggeredBy: 'AI Detection', status: 'success', startedAt: '09:22:18', duration: '2m 03s', rollbackAvailable: true, output: 'ECMP rerouting active. Traffic load balanced across 3 paths. Packet loss 0.1%.', executedBy: 'netopsai-engine' },
  { id: 'job-9868', rule: 'OSPF-Neighbor-Reset', incidentId: 'INC-4818', node: 'AGG-ARADA-01', region: 'Arada', triggeredBy: 'AI Detection', status: 'success', startedAt: '09:21:40', duration: '0m 58s', rollbackAvailable: false, output: 'OSPF neighbor cleared. DR election completed. Adjacency restored.', executedBy: 'netopsai-engine' },
  { id: 'job-9867', rule: 'CPU-Process-Kill', incidentId: 'INC-4815', node: 'CORE-NIFAS-01', region: 'Nifas Silk-Lafto', triggeredBy: 'Threshold', status: 'failed', startedAt: '09:20:11', duration: '0m 22s', rollbackAvailable: false, output: 'ERROR: Process kill rejected — insufficient privilege level. Manual intervention required.', executedBy: 'netopsai-engine' },
  { id: 'job-9866', rule: 'Link-Failover-Activate', incidentId: 'INC-4814', node: 'EDGE-YEKA-02', region: 'Yeka', triggeredBy: 'AI Detection', status: 'success', startedAt: '09:18:05', duration: '1m 34s', rollbackAvailable: true, output: 'Backup link activated. Traffic failover complete. Primary link monitoring active.', executedBy: 'netopsai-engine' },
  { id: 'job-9865', rule: 'QoS-Policy-Reapply', incidentId: 'INC-4810', node: 'EDGE-AKAKI-02', region: 'Akaki Kality', triggeredBy: 'AI Detection', status: 'success', startedAt: '08:55:14', duration: '0m 41s', rollbackAvailable: true, output: 'QoS policy service-policy-voice reapplied. DSCP marking verified.', executedBy: 'Dawit Taddese' },
  { id: 'job-9864', rule: 'NTP-Resync', incidentId: 'INC-4811', node: 'AGG-LEMIKURA-01', region: 'Lemi Kura', triggeredBy: 'Threshold', status: 'success', startedAt: '08:30:22', duration: '0m 12s', rollbackAvailable: false, output: 'NTP update-calendar executed. Clock drift corrected to ±3ms.', executedBy: 'netopsai-engine' },
  { id: 'job-9863', rule: 'BGP-Session-Restart', incidentId: 'INC-4808', node: 'CORE-ADDISKETEMA-01', region: 'Addis Ketema', triggeredBy: 'AI Detection', status: 'rolled-back', startedAt: '08:12:08', duration: '3m 22s', rollbackAvailable: false, output: 'BGP restart caused additional instability. Rollback executed — restored previous config.', executedBy: 'netopsai-engine' },
  { id: 'job-9862', rule: 'Interface-Flap-Dampen', incidentId: 'INC-4806', node: 'AGG-GULLELE-02', region: 'Gullele', triggeredBy: 'Threshold', status: 'success', startedAt: '07:55:41', duration: '0m 28s', rollbackAvailable: true, output: 'Dampening applied. Interface stability restored after 4 minutes.', executedBy: 'netopsai-engine' },
];

type SortField = 'id' | 'rule' | 'node' | 'status' | 'startedAt' | 'duration';

export default function JobHistorySection() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState<SortField>('id');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const perPage = 8;

  const filtered = jobs
    .filter((j) => {
      const q = search.toLowerCase();
      const matchQ = !q || j.id.toLowerCase().includes(q) || j.rule.toLowerCase().includes(q) || j.node.toLowerCase().includes(q) || j.incidentId.toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || j.status === statusFilter;
      return matchQ && matchStatus;
    })
    .sort((a, b) => {
      const av = String(a[sortField]);
      const bv = String(b[sortField]);
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortIcon = ({ col }: { col: SortField }) => {
    if (sortField !== col) return <SortAsc size={10} className="opacity-30 text-muted" />;
    return sortDir === 'asc' ? <SortAsc size={10} className="text-primary" /> : <SortDesc size={10} className="text-primary" />;
  };

  return (
    <div className="bg-surface border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <History size={15} className="text-primary" />
          <p className="text-sm font-semibold text-foreground">Remediation Job History</p>
          <span className="text-[10px] bg-surface-raised border border-border px-2 py-0.5 rounded-full text-muted font-mono">
            {jobs.length} jobs today
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
            <input
              type="text"
              placeholder="Search jobs…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-7 pr-3 py-1.5 text-xs bg-surface-raised border border-border rounded-lg text-foreground placeholder:text-muted focus:outline-none focus:border-primary/50 w-44 transition-colors"
            />
          </div>
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="text-xs bg-surface-raised border border-border rounded-lg pl-3 pr-7 py-1.5 text-foreground-muted focus:outline-none focus:border-primary/50 appearance-none cursor-pointer"
            >
              <option value="all">All Statuses</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="running">Running</option>
              <option value="queued">Queued</option>
              <option value="rolled-back">Rolled Back</option>
            </select>
            <ChevronDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {[
                { key: 'id' as SortField, label: 'Job ID' },
                { key: 'rule' as SortField, label: 'Rule' },
                { key: null, label: 'Incident' },
                { key: 'node' as SortField, label: 'Node' },
                { key: null, label: 'Region' },
                { key: null, label: 'Triggered By' },
                { key: 'status' as SortField, label: 'Status' },
                { key: 'startedAt' as SortField, label: 'Started' },
                { key: 'duration' as SortField, label: 'Duration' },
                { key: null, label: 'Rollback' },
                { key: null, label: '' },
              ].map(({ key, label }, i) => (
                <th
                  key={`th-job-${i}`}
                  onClick={key ? () => toggleSort(key) : undefined}
                  className={`text-left pb-2 pr-3 text-[10px] font-semibold uppercase tracking-widest text-muted whitespace-nowrap
                    ${key ? 'cursor-pointer hover:text-foreground transition-colors select-none' : ''}`}
                >
                  <div className="flex items-center gap-1">
                    {label}
                    {key && <SortIcon col={key} />}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={11} className="py-12 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <History size={28} className="text-muted" />
                    <p className="text-sm text-foreground-muted">No jobs match your filters</p>
                    <button onClick={() => { setSearch(''); setStatusFilter('all'); }} className="text-xs text-primary hover:underline">
                      Clear filters
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              paginated.map((job, idx) => (
                <React.Fragment key={job.id}>
                  <tr
                    className={`border-b border-border/40 transition-colors group
                      ${idx % 2 === 1 ? 'bg-surface-raised/20' : ''}
                      hover:bg-surface-raised/50`}
                  >
                    <td className="py-2.5 pr-3 font-mono text-[11px] text-primary font-semibold whitespace-nowrap">{job.id}</td>
                    <td className="py-2.5 pr-3 font-medium text-foreground whitespace-nowrap">{job.rule}</td>
                    <td className="py-2.5 pr-3 font-mono text-[11px] text-info whitespace-nowrap">{job.incidentId}</td>
                    <td className="py-2.5 pr-3 font-mono text-[11px] text-foreground whitespace-nowrap">{job.node}</td>
                    <td className="py-2.5 pr-3 text-foreground-muted whitespace-nowrap">{job.region}</td>
                    <td className="py-2.5 pr-3">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border uppercase tracking-wide
                        ${job.triggeredBy === 'AI Detection' ?'text-primary bg-primary/10 border-primary/20' :'text-warning bg-warning/10 border-warning/20'}`}
                      >
                        {job.triggeredBy}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3">
                      <StatusBadge status={job.status} />
                    </td>
                    <td className="py-2.5 pr-3 font-mono text-[11px] text-foreground-muted whitespace-nowrap">{job.startedAt}</td>
                    <td className="py-2.5 pr-3 font-mono text-[11px] text-foreground-muted whitespace-nowrap">{job.duration}</td>
                    <td className="py-2.5 pr-3">
                      {job.rollbackAvailable ? (
                        <button
                          onClick={() => toast.info(`Rolling back job ${job.id}…`)}
                          className="flex items-center gap-1 text-[10px] text-warning hover:text-warning/80 transition-colors"
                          title="Rollback this job"
                        >
                          <RotateCcw size={10} />
                          Rollback
                        </button>
                      ) : (
                        <span className="text-[10px] text-muted">N/A</span>
                      )}
                    </td>
                    <td className="py-2.5">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setExpandedJobId(expandedJobId === job.id ? null : job.id)}
                          className="p-1 rounded hover:bg-surface-overlay text-muted hover:text-foreground transition-colors"
                          title="View output"
                        >
                          <Eye size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedJobId === job.id && (
                    <tr key={`${job.id}-expanded`} className="bg-surface-raised/30">
                      <td colSpan={11} className="px-4 py-3">
                        <div className="flex items-start gap-2">
                          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted mt-0.5 flex-shrink-0">Output:</span>
                          <code className={`text-[11px] font-mono leading-relaxed
                            ${job.status === 'failed' ? 'text-danger' :
                              job.status === 'rolled-back' ? 'text-warning' : 'text-foreground-muted'}`}
                          >
                            {job.output}
                          </code>
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-[10px] text-muted">
                          <span>Executed by: <span className="text-foreground-muted font-mono">{job.executedBy}</span></span>
                          <span>·</span>
                          <span>Node: <span className="text-foreground-muted font-mono">{job.node}</span></span>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
        <span className="text-[11px] text-muted">
          {filtered.length} job{filtered.length !== 1 ? 's' : ''} · Page {page} of {totalPages}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-1.5 rounded text-muted hover:text-foreground hover:bg-surface-raised disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft size={13} />
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const pg = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
            return (
              <button
                key={`jobpage-${pg}`}
                onClick={() => setPage(pg)}
                className={`w-7 h-7 rounded text-[11px] font-medium transition-all
                  ${pg === page ? 'bg-primary/20 text-primary border border-primary/30' : 'text-muted hover:text-foreground hover:bg-surface-raised'}`}
              >
                {pg}
              </button>
            );
          })}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-1.5 rounded text-muted hover:text-foreground hover:bg-surface-raised disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronRight size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}